import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/sfacoctg.jpeg';
import { useLoginModal } from '../components/LoginModalContext';
import { useAuth } from '../context/AuthContext';
import PublicUserMenu from '../components/PublicUserMenu';
import { getDefaultSiteContent, getPublicMembers, getSiteContent } from '../services/authService';
import './Home.css';
import './Members.css';

const PAGE_SIZE = 20;

const EMPTY_PAGINATION = {
  currentPage: 1,
  lastPage: 1,
  perPage: PAGE_SIZE,
  total: 0,
  from: 0,
  to: 0
};

export default function Members() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteContent, setSiteContent] = useState(getDefaultSiteContent());
  const [members, setMembers] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [searchInput, setSearchInput] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { openLogin, openRegister } = useLoginModal();
  const { isAuthenticated, user, logout } = useAuth();

  const memberInitials = (name) =>
    String(name || '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('') || 'M';

  const visiblePages = useMemo(() => {
    const current = pagination.currentPage || 1;
    const last = pagination.lastPage || 1;
    let start = Math.max(1, current - 2);
    let end = Math.min(last, start + 4);
    start = Math.max(1, end - 4);
    const pages = [];
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
    return pages;
  }, [pagination.currentPage, pagination.lastPage]);

  const loadMembers = async (page, query = activeQuery) => {
    setLoading(true);
    setError('');
    try {
      const response = await getPublicMembers(page, PAGE_SIZE, query);
      setMembers(Array.isArray(response?.members) ? response.members : []);
      setPagination(response?.pagination || EMPTY_PAGINATION);
    } catch (err) {
      setMembers([]);
      setPagination(EMPTY_PAGINATION);
      setError(err.message || 'Unable to load members.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSiteContent()
      .then((data) => setSiteContent(data))
      .catch(() => {});
    loadMembers(1, '');
  }, []);

  const goToPage = (page) => {
    if (loading) return;
    if (page < 1 || page > pagination.lastPage || page === pagination.currentPage) return;
    loadMembers(page, activeQuery);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const nextQuery = searchInput.trim();
    setActiveQuery(nextQuery);
    loadMembers(1, nextQuery);
  };

  const clearSearch = () => {
    setSearchInput('');
    setActiveQuery('');
    loadMembers(1, '');
  };

  return (
    <div className="members-page">
      <div className="dashboard-topbar">
        <div className="dashboard-topbar-left">
          <span>{siteContent.companyName}</span>
          <span className="dot" />
          <span>{siteContent.topHeaderAddress}</span>
          <span className="dot" />
          <span>{siteContent.topHeaderEmail}</span>
          <span className="dot" />
          <span>{siteContent.topHeaderPhone}</span>
        </div>
        <div className="dashboard-topbar-right">
          <a href={siteContent.linkedinUrl} target="_blank" rel="noreferrer">LinkedIn</a>
          <a href={siteContent.instagramUrl} target="_blank" rel="noreferrer">Instagram</a>
          <a href={siteContent.facebookUrl} target="_blank" rel="noreferrer">Facebook</a>
          <a href={siteContent.whatsappUrl} target="_blank" rel="noreferrer">WhatsApp</a>
        </div>
      </div>

      <header className="dashboard-header">
        <div className="brand">
          <img className="brand-logo" src={siteContent.logoUrl || logo} alt="Safco CTG logo" />
          <div>
            <p className="brand-title">{siteContent.brandTitle}</p>
            <p className="brand-subtitle">{siteContent.brandSubtitle}</p>
          </div>
        </div>
        <nav className={`dashboard-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <Link to="/">Home</Link>
          <Link to="/members">Members</Link>
          <Link to="/stories">Stories</Link>
          <Link to="/about">About</Link>
          <Link to="/contact">Contact</Link>
          <div className="mobile-actions">
            {!isAuthenticated && (
              <>
                <button className="ghost-button" type="button" onClick={openLogin}>Login</button>
                <button className="ghost-button" type="button" onClick={openRegister}>Register</button>
              </>
            )}
            {isAuthenticated && (
              <>
                <Link className="ghost-button" to={user?.role === 'admin' ? '/dashboard/admin' : '/dashboard/member'}>Dashboard</Link>
                <button className="ghost-button" type="button" onClick={logout}>Logout</button>
              </>
            )}
            <Link className="ghost-button" to="/submit-payment">Donate</Link>
            <Link className="solid-button" to="/submit-payment">Submit Payment</Link>
          </div>
        </nav>
        <div className="dashboard-actions">
          {!isAuthenticated && (
            <>
              <button className="ghost-button" type="button" onClick={openLogin}>Login</button>
              <button className="ghost-button" type="button" onClick={openRegister}>Register</button>
            </>
          )}
          {isAuthenticated && (
            <PublicUserMenu />
          )}
          <Link className="ghost-button" to="/submit-payment">Donate</Link>
          <Link className="solid-button" to="/submit-payment">Submit Payment</Link>
        </div>
        <button
          className="menu-toggle"
          type="button"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      <main className="members-page-main">
        <section className="dashboard-section members-list-section">
          <div className="section-heading">
            <h1>Registered Members</h1>
            <p>Showing approved and active alumni members.</p>
          </div>

          <form className="members-search-form" onSubmit={submitSearch}>
            <input
              type="text"
              placeholder="Search by name, organization, designation..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <button type="submit" className="solid-button members-search-btn" disabled={loading}>
              Search
            </button>
            <button
              type="button"
              className="ghost-button members-search-btn"
              onClick={clearSearch}
              disabled={loading || (!searchInput && !activeQuery)}
            >
              Clear
            </button>
          </form>

          {activeQuery && (
            <p className="members-active-query">Search: "{activeQuery}"</p>
          )}

          <p className="members-range">
            {pagination.total
              ? `Showing ${pagination.from}-${pagination.to} of ${pagination.total} members`
              : 'No members found'}
          </p>

          {error && <p className="members-error">{error}</p>}

          {loading ? (
            <p className="members-loading">Loading members...</p>
          ) : (
            <div className="members-grid">
              {members.map((member) => (
                <article key={member.id || member.name} className="member-card">
                  <div className="member-avatar">
                    {member.profilePhotoUrl ? (
                      <img src={member.profilePhotoUrl} alt={member.name} />
                    ) : (
                      <span>{memberInitials(member.name)}</span>
                    )}
                  </div>
                  <div className="member-card-body">
                    <h3>{member.name}</h3>
                    <p className="member-role">{member.designation || 'Member'}</p>
                    <p className="member-org">{member.currentOrganization || 'Organization not shared'}</p>
                  </div>
                </article>
              ))}
            </div>
          )}

          <div className="members-pagination" aria-label="Members pagination">
            <button
              type="button"
              className="ghost-button members-page-btn"
              onClick={() => goToPage(pagination.currentPage - 1)}
              disabled={loading || pagination.currentPage <= 1}
            >
              Previous
            </button>
            <div className="members-page-numbers">
              {visiblePages.map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`members-number-btn ${page === pagination.currentPage ? 'active' : ''}`}
                  onClick={() => goToPage(page)}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="ghost-button members-page-btn"
              onClick={() => goToPage(pagination.currentPage + 1)}
              disabled={loading || pagination.currentPage >= pagination.lastPage}
            >
              Next
            </button>
          </div>
        </section>
      </main>

      <footer className="dashboard-footer">
        <div>
          <img className="footer-logo" src={logo} alt="Safco CTG logo" />
          <h3>SFACO-CTG</h3>
          <p>
            Building lifelong connections between graduates, students, and faculty.
          </p>
        </div>
        <div className="footer-links">
          <div>
            <h4>Community</h4>
            <Link to="/contact">Contact</Link>
            <Link to="/">Home</Link>
            <Link to="/members">Members</Link>
          </div>
          <div>
            <h4>Support</h4>
            <a href="mailto:alumni@chatar.org">Email us</a>
            <a href="tel:+16175550142">Call office</a>
            <Link to="/submit-payment">Donate</Link>
          </div>
          <div>
            <h4>Resources</h4>
            <Link to="/stories">Newsroom</Link>
            <Link to="/about">About</Link>
            <Link to="/register">Register</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
